import {
  AccountBalanceOutlined,
  InboxOutlined,
  PendingActionsOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import { Box, Button, Chip, CircularProgress, Pagination, Typography } from "@mui/material";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import ApproversPanel from "@/components/approvals/ApproversPanel";
import ConfirmDecisionDialog from "@/components/approvals/ConfirmDecisionDialog";
import FundingAttentionStrip from "@/components/topups/FundingAttentionStrip";
import { GfgButton } from "@/components/ui";
import { useApprovalQueue, useCancelApproval, useDecideApproval } from "@/hooks/useAdjustments";
import type { QueueItem } from "@/interfaces/adjustments";
import { formatDateLong, formatDateTime } from "@/utils/date-format";

/**
 * Action Dashboard — what is waiting on you, and what you are waiting on others for.
 *
 * Today this is the approval queue only. The design also puts open workflow steps here (invoicing,
 * SOW, contracting) as one live query; those tables do not exist yet, so this renders the queue half
 * and is shaped to take the other beside it.
 *
 * Nothing on this page knows what a credit adjustment is. A row's headline, its facts and its
 * confirm wording all come from the summarizer that its own action type registered, which is what
 * lets a second reviewable action appear here without touching this file.
 */

/** How long a request has been waiting, in whole days. Drives the stale marker and the sort. */
function ageDays(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

/** Past this, a request has been sitting long enough to say so on its date header. */
const STALE_DAYS = 3;

/** Requests are grouped by the DAY they were raised, so the key is the date alone. */
const dayKey = (iso: string) => new Date(iso).toISOString().slice(0, 10);

/**
 * The heading over a day's requests. "Today" and "Yesterday" read faster than a date for the two
 * cases that are almost always on screen; everything older gets the unambiguous long form.
 */
function dayTitle(iso: string): string {
  const days = ageDays(iso);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return formatDateLong(iso);
}

/**
 * A filter tile: one per action type on the queue, with its open count.
 *
 * Built from what is actually in the queue rather than a hard-coded list, so a new reviewable
 * action gets a tile the day it starts being raised.
 */
function FilterTile({
  label,
  count,
  icon,
  subtitle,
  active,
  onClick,
}: {
  label: string;
  count: number;
  icon: ReactNode;
  subtitle: string;
  active: boolean;
  onClick: () => void;
}) {
  // The dashboard's highlight card (MetricCard) — uppercase dust-ink label with its icon, the count
  // large, a line beneath — made a button, with the selected filter outlined in dark dust.
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-w-[200px] flex-1 cursor-pointer rounded-2xl border bg-white p-4 text-left font-lato transition-colors ${
        active ? "border-dark-dust bg-clear-dust" : "border-[#e5e5e5] hover:bg-clear-dust"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-nowrap text-[11px] font-bold uppercase tracking-[2px] text-dust-ink">
          {label}
        </span>
        <span className="flex items-center pb-1 text-primary-active">{icon}</span>
      </div>
      <div className="mb-1 text-[2.25rem] font-semibold leading-[2.25rem] tracking-[-0.75px] text-[#333]">
        {count.toLocaleString()}
      </div>
      <div className="overflow-hidden text-ellipsis whitespace-nowrap text-[14px] text-graphite">
        {subtitle}
      </div>
    </button>
  );
}

/** The icon a tile carries — by action type, with a neutral fallback for one added later. */
const TILE_ICON: Record<string, ReactNode> = {
  payment_terms_change: <AccountBalanceOutlined />,
  admin_adjustment: <TuneOutlined />,
};

/** "23 Sep, 11:04" — how the payment-terms card dates a request. */
function shortStamp(iso: string): string {
  const d = new Date(iso);
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return `${d.getDate()} ${d.toLocaleString("en-US", { month: "short" })}, ${time}`;
}

/**
 * A request whose summary brings its own context column (a payment-terms change: the settings on
 * the left, the client's exposure on the right, the maker's note beneath). Laid out as its design
 * (Postpaid mockup 2); every other action keeps the compact card.
 */
function LedgerCard({
  item,
  onDecide,
  onWithdraw,
  busy,
}: {
  item: QueueItem;
  onDecide: (approve: boolean) => void;
  onWithdraw: () => void;
  busy: boolean;
}) {
  const s = item.summary;
  const row = (f: { label: string; value: string; tone?: "warn" | "muted" }) => (
    <div
      key={f.label}
      className="flex justify-between gap-4 border-card-border border-b py-[9px] text-[14px] last:border-b-0"
    >
      <span>{f.label}</span>
      <span
        className={`font-semibold tabular-nums ${
          f.tone === "warn" ? "text-good-wine" : f.tone === "muted" ? "text-graphite" : ""
        }`}
      >
        {f.value}
      </span>
    </div>
  );
  return (
    <div className="rounded-[10px] border border-card-border bg-white p-5 font-lato text-rodeo-black">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="m-0 font-crimson text-[22px] font-normal leading-tight">{s?.headline}</h3>
          <div className="mt-0.5 text-[12px] text-graphite">
            Requested by {item.requestedByName ?? "unknown"} · {shortStamp(item.createdAt)} · any
            approver may action it
          </div>
        </div>
        <span className="shrink-0 rounded-full border border-good-ochre-border bg-good-ochre-bg px-[11px] py-1 text-[11px] font-bold uppercase tracking-[1px] text-good-ochre">
          {item.actionLabel}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-x-4 md:grid-cols-2">
        <div>{(s?.facts ?? []).map(row)}</div>
        <div>{(s?.sideFacts ?? []).map(row)}</div>
      </div>
      {s?.note && (
        <div className="mt-3 rounded-[6px] border border-good-night-4 bg-good-night-bg px-3.5 py-3 text-[13px] leading-[1.55] text-[#24404f]">
          “{s.note}” — maker's note
        </div>
      )}
      {item.actionable ? (
        <div className="mt-3 flex gap-2.5">
          <GfgButton variant="accent" disabled={busy} onClick={() => onDecide(true)}>
            Approve
          </GfgButton>
          <GfgButton
            variant="outline"
            className="border-good-wine text-good-wine"
            disabled={busy}
            onClick={() => onDecide(false)}
          >
            Reject with note
          </GfgButton>
        </div>
      ) : item.isMine ? (
        <div className="mt-3 flex gap-2.5">
          <GfgButton variant="secondary" disabled={busy} onClick={onWithdraw}>
            Withdraw
          </GfgButton>
        </div>
      ) : null}
    </div>
  );
}

function QueueCard({
  item,
  onDecide,
  onWithdraw,
  busy,
}: {
  item: QueueItem;
  onDecide: (approve: boolean) => void;
  onWithdraw: () => void;
  busy: boolean;
}) {
  if (item.summary?.sideFacts?.length) {
    return <LedgerCard item={item} onDecide={onDecide} onWithdraw={onWithdraw} busy={busy} />;
  }
  const negative = item.summary?.variant === "negative";

  return (
    <Box
      sx={{
        border: "1px solid var(--color-card-border)",
        // A removal is the one worth pausing on, so it is scannable without reading the sentence.
        borderLeft: `4px solid ${
          negative ? "var(--color-error, #b3261e)" : "var(--color-dark-dust, #a08e6f)"
        }`,
        borderRadius: "var(--radius-card, 8px)",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        bgcolor: "var(--color-white)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, flex: 1 }}>
          {item.summary?.headline ?? `${item.actionType} #${item.actionId}`}
        </Typography>
        {item.isMine && <Chip size="small" variant="outlined" label="Your request" />}
      </Box>

      {item.summary?.detail ? (
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
          {item.summary.detail}
        </Typography>
      ) : null}

      {(item.summary?.facts ?? []).length > 0 ? (
        <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mt: 0.25 }}>
          {item.summary?.facts?.map((f) => (
            <Box key={f.label}>
              <Typography sx={{ fontSize: 11, color: "var(--color-graphite)", opacity: 0.8 }}>
                {f.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: f.tone === "warn" ? 700 : 600,
                  color: f.tone === "warn" ? "var(--color-error, #b3261e)" : undefined,
                }}
              >
                {f.value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", opacity: 0.85 }}>
        Raised by {item.requestedByName ?? "unknown"} · {formatDateTime(item.createdAt)}
      </Typography>

      {item.actionable ? (
        <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
          <Button variant="contained" size="small" disabled={busy} onClick={() => onDecide(true)}>
            Approve
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={busy}
            onClick={() => onDecide(false)}
          >
            Reject
          </Button>
        </Box>
      ) : item.isMine ? (
        // Withdrawing is the MAKER's own act. Anyone else — including a non-approver — gets nothing.
        <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
          <Button variant="outlined" size="small" disabled={busy} onClick={onWithdraw}>
            Withdraw
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

/** One page of requests, at most PAGE_SIZE — enough that a busy queue never becomes a scroll. */
const PAGE_SIZE = 10;

/**
 * Who the request is for, as a filter rather than three stacked lists.
 *
 * It was three sections. Grouping by DAY and by audience at once needs two levels of heading, and on
 * a page of ten that is more heading than content — so the audience became a filter and the day
 * stayed the heading.
 */
type Audience = "all" | "mine_to_decide" | "mine_raised";

const AUDIENCE_LABEL: Record<Audience, string> = {
  all: "Everything",
  mine_to_decide: "Waiting on you",
  mine_raised: "Your requests",
};

function matchesAudience(i: QueueItem, a: Audience): boolean {
  if (a === "mine_to_decide") return i.actionable;
  if (a === "mine_raised") return i.isMine;
  return true;
}

function Queue() {
  const { data: queue = [], isLoading } = useApprovalQueue();
  const decide = useDecideApproval();
  const cancel = useCancelApproval();

  const [type, setType] = useState<string | null>(null);
  const [audience, setAudience] = useState<Audience>("all");
  const [oldestFirst, setOldestFirst] = useState(true);
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<{ item: QueueItem; approve: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // A tile per action type PRESENT on the queue, so a new reviewable action needs no change here.
  const tiles = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const i of queue) {
      const seen = counts.get(i.actionType);
      if (seen) seen.count += 1;
      else counts.set(i.actionType, { label: i.actionLabel, count: 1 });
    }
    return [...counts.entries()].map(([key, v]) => ({ key, ...v }));
  }, [queue]);

  const filtered = useMemo(() => {
    const rows = queue.filter(
      (i) => (type === null || i.actionType === type) && matchesAudience(i, audience),
    );
    return rows.sort((a, b) => {
      const d = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return oldestFirst ? d : -d;
    });
  }, [queue, type, audience, oldestFirst]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // A filter change can leave you past the end — snap back rather than showing an empty page.
  useEffect(() => {
    if (page > pageCount) setPage(1);
  }, [page, pageCount]);

  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /** The page's rows under their day, in the order the sort produced. */
  const days = useMemo(() => {
    const out: { key: string; items: QueueItem[] }[] = [];
    for (const i of pageItems) {
      const key = dayKey(i.createdAt);
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(i);
      else out.push({ key, items: [i] });
    }
    return out;
  }, [pageItems]);

  const busy = decide.isPending || cancel.isPending;

  async function withdraw(id: number) {
    setError(null);
    try {
      await cancel.mutateAsync(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not withdraw that request");
    }
  }

  if (isLoading) return <CircularProgress size={24} />;

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <FilterTile
          label="Everything open"
          count={queue.length}
          icon={<InboxOutlined />}
          subtitle="Waiting on a decision"
          active={type === null}
          onClick={() => {
            setType(null);
            setPage(1);
          }}
        />
        {tiles.map((t) => (
          <FilterTile
            key={t.key}
            label={t.label}
            count={t.count}
            icon={TILE_ICON[t.key] ?? <PendingActionsOutlined />}
            subtitle={`${t.count === 1 ? "request" : "requests"} open`}
            active={type === t.key}
            onClick={() => {
              setType(type === t.key ? null : t.key);
              setPage(1);
            }}
          />
        ))}
      </Box>

      {queue.length === 0 ? (
        <Typography sx={{ color: "var(--color-graphite)" }}>Nothing is waiting.</Typography>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            {(Object.keys(AUDIENCE_LABEL) as Audience[]).map((a) => (
              <Chip
                key={a}
                size="small"
                label={`${AUDIENCE_LABEL[a]} (${queue.filter((i) => matchesAudience(i, a)).length})`}
                variant={audience === a ? "filled" : "outlined"}
                onClick={() => {
                  setAudience(a);
                  setPage(1);
                }}
              />
            ))}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              onClick={() => {
                setOldestFirst((v) => !v);
                setPage(1);
              }}
            >
              {oldestFirst ? "Oldest first ↑" : "Newest first ↓"}
            </Button>
          </Box>

          {error ? (
            <Typography sx={{ fontSize: 13, color: "var(--color-error, #b3261e)", mb: 1.5 }}>
              {error}
            </Typography>
          ) : null}

          {filtered.length === 0 ? (
            <Typography sx={{ color: "var(--color-graphite)" }}>
              Nothing matches that filter.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3.5 }}>
              {days.map((d) => (
                <Box key={d.key} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 13,
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                        opacity: 0.7,
                      }}
                    >
                      {dayTitle(d.items[0]?.createdAt ?? d.key)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", opacity: 0.7 }}>
                      ({d.items.length})
                    </Typography>
                    {ageDays(d.items[0]?.createdAt ?? d.key) >= STALE_DAYS ? (
                      <Chip size="small" color="warning" variant="outlined" label="waiting" />
                    ) : null}
                  </Box>
                  {d.items.map((i) => (
                    <QueueCard
                      key={i.id}
                      item={i}
                      busy={busy}
                      onDecide={(approve) => setPending({ item: i, approve })}
                      onWithdraw={() => withdraw(i.id)}
                    />
                  ))}
                </Box>
              ))}

              {pageCount > 1 ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(_, p) => setPage(p)}
                    size="small"
                  />
                </Box>
              ) : null}
            </Box>
          )}
        </>
      )}

      <ConfirmDecisionDialog
        item={pending?.item ?? null}
        approve={pending?.approve ?? true}
        busy={decide.isPending}
        onClose={() => setPending(null)}
        onConfirm={async (note) => {
          if (!pending) return;
          await decide.mutateAsync({ id: pending.item.id, approve: pending.approve, note });
          setPending(null);
        }}
      />
    </>
  );
}

type ActionsTab = "queue" | "approvers" | "alerts";

function TabButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        appearance: "none",
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: "10px 4px",
        marginBottom: -1,
        fontFamily: "var(--font-lato)",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.2px",
        color: active ? "var(--color-rodeo-black)" : "var(--color-graphite)",
        borderBottom: `2px solid ${active ? "var(--color-dark-dust)" : "transparent"}`,
        transition: "all 0.15s",
      }}
    >
      {label}
    </button>
  );
}

export default function Actions() {
  const [tab, setTab] = useState<ActionsTab>("queue");

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, fontFamily: "var(--font-lato)" }}>
      {/* No page heading: the topbar already titles the route, the way Finance does. */}
      <Typography sx={{ fontSize: 14, color: "var(--color-graphite)", mb: 3 }}>
        Requests waiting on a decision, and campaigns whose funding needs a look. Nothing in the
        queue has changed a balance yet.
      </Typography>

      <div
        role="tablist"
        style={{
          display: "flex",
          gap: 28,
          borderBottom: "1px solid var(--color-card-border)",
          marginBottom: 24,
        }}
      >
        <TabButton active={tab === "queue"} label="Actions Queue" onClick={() => setTab("queue")} />
        <TabButton
          active={tab === "approvers"}
          label="Approvers"
          onClick={() => setTab("approvers")}
        />
        {/* Its own tab, not a strip above the others: a failing card is not an approval and does not
            belong in a queue of decisions, but it is also not urgent enough to sit over the top of
            the page every time somebody opens it. */}
        <TabButton
          active={tab === "alerts"}
          label="Alerts Queue"
          onClick={() => setTab("alerts")}
        />
      </div>

      {tab === "queue" ? (
        <Queue />
      ) : tab === "approvers" ? (
        <ApproversPanel />
      ) : (
        <FundingAttentionStrip />
      )}
    </Box>
  );
}
